import { useMemo } from 'react';
import { MODULES, type ModuleId, type ModuleData, getFamiliesForModule, getAllowedTiersForGroup } from '../../content';
import { getFamilyMasteryKey } from '../../mastery';
import { useAppStore } from '../../app/hooks/useAppStore';
import type { FamilyPickerMode, FamilyWithMastery } from './types';
export interface ModuleAggregate {
  module: ModuleData;
  count: number;
  avgMastery: number;
}
export interface UseFamilyPickerDataResult {
  allFamilies: FamilyWithMastery[];
  moduleAggregates: ModuleAggregate[];
  filteredFamilies: FamilyWithMastery[];
  hasPracticedFamilies: boolean;
  hasChallengeReadyFamilies: boolean;
  /** Eerste item van de gesorteerde lijst — een snelle ingang, geen advies. */
  quickStart: FamilyWithMastery | null;
  /** Mag de snelle ingang binnen dit vak apart bovenaan? */
  showQuickStartInModule: boolean;
  /** De groep van de leerling — bepaalt of een niveaubadge betekenis heeft. */
  learnerGroup: number;
}

/**
 * useFamilyPickerData — alle selectie- en filterlogica van de onderwerpkiezer.
 *.
 * Stond als vijf achter elkaar geketende `useMemo`-blokken bovenaan een
 * component van bijna 700 regels, tussen de JSX van drie kaartvarianten. Wie
 * daar de sorteerregel van Sprint wilde nalezen, moest eerst door de opmaak.
 *
 * De filters zijn bewust per modus verschillend en dat verschil is didactisch:
 * Sprint laat alleen onderwerpen zien waar het kind al aan begonnen is (anders
 * is er niets om op te focussen), Uitdaging alleen wat het al beheerst, en Vrij
 * Oefenen alles — inclusief tiers buiten de eigen groep, want dat is precies
 * wat "vrij" betekent.
 */
export function useFamilyPickerData(mode: FamilyPickerMode, selectedModule: ModuleId | null, searchQuery: string): UseFamilyPickerDataResult {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const familyMastery = state.familyMastery || {};
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  const allFamilies = useMemo(() => {
    const result: FamilyWithMastery[] = [];
    for (const module of MODULES) {
      for (const family of getFamiliesForModule(module.id)) {
        if (mode !== 'free' && !allowedTiers.has(family.tier)) continue;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey] || null;
        const masteryScore = mastery?.masteryScore ?? 0;
        const attempts = mastery?.attempts ?? 0;
        if (mode === 'challenge' && masteryScore < 70) continue;
        if (mode === 'sprint' && attempts === 0) continue;
        result.push({
          family,
          mastery,
          masteryScore,
          attempts
        });
      }
    }
    if (mode === 'sprint') {
      // Zwakste eerst: bij Sprint is dat de reden dat het onderwerp er staat.
      result.sort((a, b) => a.masteryScore - b.masteryScore);
    } else if (mode === 'challenge') {
      // Sterkste eerst: een uitdaging bouwt op wat al zit.
      result.sort((a, b) => b.masteryScore - a.masteryScore);
    } else {
      result.sort((a, b) => a.family.description.localeCompare(b.family.description));
    }
    return result;
  }, [selectedGroup, familyMastery, allowedTiers, mode]);
  const moduleAggregates = useMemo(() => MODULES.map(module => {
    const families = allFamilies.filter(f => f.family.module === module.id);
    const count = families.length;
    return {
      module,
      count,
      avgMastery: count > 0 ? families.reduce((sum, f) => sum + f.masteryScore, 0) / count : 0
    };
  }).filter(m => m.count > 0), [allFamilies]);
  const selectedModuleFamilies = useMemo(() => selectedModule ? allFamilies.filter(f => f.family.module === selectedModule) : [], [allFamilies, selectedModule]);
  const filteredFamilies = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return selectedModuleFamilies;
    return selectedModuleFamilies.filter(({
      family
    }) => family.familyId.toLowerCase().includes(query) || family.description.toLowerCase().includes(query) || family.skill.toLowerCase().includes(query) || family.subSkill.toLowerCase().includes(query));
  }, [selectedModuleFamilies, searchQuery]);
  const hasPracticedFamilies = useMemo(() => Object.values(familyMastery).some(m => m.attempts > 0), [familyMastery]);
  const hasChallengeReadyFamilies = useMemo(() => Object.values(familyMastery).some(m => m.masteryScore >= 70), [familyMastery]);
  const quickStart = allFamilies.length > 0 ? allFamilies[0] : null;
  return {
    allFamilies,
    moduleAggregates,
    filteredFamilies,
    hasPracticedFamilies,
    hasChallengeReadyFamilies,
    quickStart,
    learnerGroup: Number(selectedGroup),
    showQuickStartInModule: Boolean(!searchQuery && selectedModule && filteredFamilies.length > 0 && quickStart && filteredFamilies[0].family.familyId === quickStart.family.familyId)
  };
}